<template>
  <div v-if="currentUser" class="quotes-tab">
    <Transition>
      <div v-if="currentQuote">
        <QuoteBuilder v-if="currentQuote" :quote="currentQuote">
          <template #title>
            <h3 class="flex items-center flex-wrap">
              <Breadcrumbs
                :crumbs="crumbs"
                :root-text="rootName"
                @click="handleCrumbClick"
              />
              <span
                v-if="currentQuote?.invoice_id"
                class="font-italic ml-1 hint-crumb"
              >
                ({{ displayQuoteStatus(currentQuote.status) }} &mdash;
                <a class="invoice-link" @click="navigateToInvoice"
                  >Invoice #{{ currentQuote.invoice_id }}</a
                >)
              </span>
            </h3>
          </template>
        </QuoteBuilder>
      </div>
      <div v-else-if="!loading">
        <CustomerQuotesDatatable
          :source="source"
          :filters="filters"
          :customer-id="currentUser.id"
          :address="address"
          :show-sub-customers="currentUser.has_subs"
          flat
          @row-click="handleClick"
          @view-quote="viewQuote"
        >
          <template #actions>
            <sera-button
              icon="plus"
              iconRight
              label="New Quote"
              @click="handleCreate"
            />
          </template>
        </CustomerQuotesDatatable>
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Button from "@/components/Button.vue";
import Transition from "@/components/Transition.vue";
import QuoteBuilder from "@/Job/QuoteBuilder/QuoteBuilder.vue";
import CustomerQuotesDatatable from "./CustomerQuotesDatatable.vue";

const isValidId = (value) => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0;
};

export default {
  components: {
    CustomerQuotesDatatable,
    Transition,
    Button,
    QuoteBuilder
  },
  props: {
    address: {
      type: Object,
      default: null
    },
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      crumbs: [],
      paymentModal: false
    };
  },
  computed: {
    ...mapGetters(["financingEnabled"]),
    ...mapState(["currentQuote", "currentUser"]),
    ...mapState({
      rootName() {
        return this.crumbs.length ? "Back to Quotes" : "Quotes";
      }
    })
  },
  watch: {
    currentUser(newValue) {
      if (newValue) {
        const params = getQueryVariables();
        // loads the quote when page starts, after it starts it shouldn't do anything
        if (params.quote_id && !this.currentQuote) {
          if (!isValidId(params.quote_id)) {
            updateQueryParam("quote_id", null);
            return;
          }
          this.loadQuote({
            quoteId: params.quote_id,
            customerId: params.customer_id ?? newValue.id,
            editing: params.editing
          });
        }
      }
    },
    currentQuote(newValue) {
      if (newValue) {
        this.updateCrumbs(newValue);
      }
    }
  },
  mounted() {
    EventBus.$on("datatable-edit-quote", this.loadQuote);
    EventBus.$on("datatable-show-quote", this.loadQuote);
    window.addEventListener("popstate", this.navigateBack);
  },
  beforeUnmount() {
    EventBus.$off("datatable-edit-quote", this.loadQuote);
    EventBus.$off("datatable-show-quote", this.loadQuote);
    window.removeEventListener("popstate", this.navigateBack);
    this.setCurrentQuote(null);
  },
  methods: {
    ...mapActions(["createQuote", "fetchQuote", "setCurrentQuote"]),
    handleCreate() {
      if (!this.currentUser.primary_address) {
        window.notify.error(
          "Unable to create a quote because this account does not have a billing address!"
        );
        return;
      }
      this.createQuote({
        customerId: this.currentUser.id,
        data: {
          service_address_id: this.address
            ? this.address.id
            : this.currentUser.primary_address.id
        }
      }).then((res) => {
        window.notify.success("Quote created successfully!");
        // Update currentQuote and set editing to true
        this.selectQuote({
          ...res.data,
          editing: true
        });
      });
    },
    handleClick(row) {
      this.viewQuote(row.DT_RowId, row.customer_id);
    },
    viewQuote(quoteId, customerId) {
      this.loadQuote({ quoteId, customerId });
    },
    handlePaymentsClick() {
      this.paymentModal = !this.paymentModal;
      // Refresh invoice after closing payment modal
      // to reflect any payments made
      if (!this.paymentModal) {
        this.loadQuote({ quoteId: this.currentQuote.id });
      }
    },
    loadQuote({ quoteId, editing, customerId }) {
      this.loading = true;
      // Some quote-open events (e.g. the datatable quote link) omit customerId;
      // fall back to the profile customer so we never request /customers/undefined.
      this.fetchQuote({
        quoteId,
        customerId: customerId ?? this.currentUser?.id
      })
        .then((res) => {
          this.selectQuote({
            ...res.data,
            editing
          });
        })
        .catch(() => {
          this.setCurrentQuote(null);
          updateQueryParam("quote_id", null);
        })
        .finally(() => (this.loading = false));
    },
    handleCrumbClick(crumb) {
      if (crumb.id === "root") {
        this.setCurrentQuote(null);
        updateQueryParam("quote_id", null);
      }
    },
    selectQuote(quote) {
      this.setCurrentQuote(quote);
      updateQueryParam("quote_id", isValidId(quote?.id) ? quote.id : null);
    },
    updateCrumbs(quote) {
      this.crumbs = [
        {
          id: quote.id,
          label: `Quote #${quote.id}`,
          hint: quote.invoice_id ? null : this.displayQuoteStatus(quote.status)
        }
      ];
    },
    navigateToInvoice() {
      const invoiceId = this.currentQuote.invoice_id;
      EventBus.$emit("set-selected-tab", "Invoices");
      this.$nextTick(() => {
        EventBus.$emit("navigate-to-invoice", invoiceId);
      });
    },
    navigateBack() {
      const params = getQueryVariables();
      if (!isValidId(params.quote_id)) {
        this.setCurrentQuote(null);
        updateQueryParam("quote_id", null);
      } else {
        const customerId = params.customer_id ?? this.currentUser?.id;
        this.viewQuote(params.quote_id, customerId);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.quotes-tab:empty {
  min-height: 100vh;
}

.hint-crumb {
  text-transform: capitalize;
}

.invoice-link {
  font-style: normal;
  color: var(--lightblue);
  cursor: pointer;
  text-transform: none;
}
</style>
